import React, { useEffect, useState, useRef } from 'react';
import { UploadIcon, CheckCircle2Icon, ChevronDownIcon, ChevronRightIcon, BuildingIcon, CheckIcon } from 'lucide-react';
import { Agency, PropertyData, LoanData, Parties, PartyContact } from '../types';
import { mockPropertyData, mockLoanData, mockParties, mockAgencies } from '../data/mockData';
interface OrderFormPageProps {
  onSubmit: (agency: Agency) => void;
}
export function OrderFormPage({
  onSubmit
}: OrderFormPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Accordion State
  const [expandedSection, setExpandedSection] = useState<string | null>('agency');
  // Agency Selection State
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [isAgencyDropdownOpen, setIsAgencyDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Form State (pre-filled from mock data)
  const [property, setProperty] = useState<PropertyData>(mockPropertyData);
  const [loan, setLoan] = useState<LoanData>(mockLoanData);
  const [parties, setParties] = useState<Parties>(mockParties);
  const [notes, setNotes] = useState('');
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsAgencyDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSubmit = () => {
    if (!selectedAgency) {
      alert('Please select a settlement agency first.');
      setExpandedSection('agency');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      setTimeout(() => {
        onSubmit(selectedAgency);
      }, 2000);
    }, 1500);
  };
  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };
  if (showSuccess) {
    return <div className="h-full flex flex-col items-center justify-center bg-slate-50" data-id="element-206">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center max-w-md w-full" data-id="element-207">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 mb-5" data-id="element-208">
            <CheckCircle2Icon className="h-7 w-7 text-emerald-600" data-id="element-209" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-210">
            Order Placed Successfully!
          </h2>
          <p className="text-sm text-slate-600 mb-6" data-id="element-211">
            Your title order has been sent to {selectedAgency?.name}.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200" data-id="element-212">
            <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-medium" data-id="element-213">
              Order Number
            </p>
            <p className="text-lg font-mono font-bold text-slate-900" data-id="element-214">
              TO-2024-8891
            </p>
          </div>
          <div className="animate-pulse flex items-center justify-center text-xs text-blue-600 font-medium" data-id="element-215">
            Redirecting to Dashboard...
          </div>
        </div>
      </div>;
  }
  const updatePartyContact = (group: keyof Parties, index: number, field: keyof PartyContact, value: string) => {
    setParties(prev => {
      const updated = [...prev[group]];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return {
        ...prev,
        [group]: updated
      };
    });
  };
  const addPartyContact = (group: keyof Parties) => {
    const newContact: PartyContact = {
      _id: `new-${Date.now()}`,
      entity_type: 'INDIVIDUAL',
      first_name: '',
      last_name: '',
      cell_phone: '',
      email: ''
    };
    setParties(prev => ({
      ...prev,
      [group]: [...prev[group], newContact]
    }));
  };
  const removePartyContact = (group: keyof Parties, index: number) => {
    setParties(prev => ({
      ...prev,
      [group]: prev[group].filter((_, i) => i !== index)
    }));
  };
  const renderPartyGroup = (label: string, group: keyof Parties, borderColor: string) => <div data-id="element-216">
      <div className="flex items-center justify-between mb-4" data-id="element-217">
        <h3 className="text-sm font-semibold text-slate-800" data-id="element-218">{label}</h3>
        <button type="button" onClick={() => addPartyContact(group)} className="text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-md px-2.5 py-1 transition-colors hover:bg-blue-50" data-id="element-219">
          + Add
        </button>
      </div>
      {parties[group].length === 0 ? <p className="text-xs text-slate-400 italic" data-id="element-220">
          No {label.toLowerCase()} added yet.
        </p> : <div className="space-y-4" data-id="element-221">
          {parties[group].map((contact, idx) => <div key={contact._id} className={`bg-white p-4 rounded-lg border border-slate-200 border-l-2 ${borderColor} shadow-sm`} data-id="element-222">
              <div className="grid grid-cols-1 gap-y-5 gap-x-4 sm:grid-cols-6" data-id="element-223">
                {/* Entity Type Toggle */}
                <div className="sm:col-span-4" data-id="element-224">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-225">
                    Entity Type
                  </label>
                  <div className="flex rounded-lg bg-slate-100 p-1" data-id="element-226">
                    <button type="button" onClick={() => updatePartyContact(group, idx, 'entity_type', 'INDIVIDUAL')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${contact.entity_type === 'INDIVIDUAL' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-227">
                      Individual
                    </button>
                    <button type="button" onClick={() => updatePartyContact(group, idx, 'entity_type', 'ORGANIZATION')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${contact.entity_type === 'ORGANIZATION' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-228">
                      Organization
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2 flex items-end" data-id="element-229">
                  <button type="button" onClick={() => removePartyContact(group, idx)} className="text-xs font-medium text-red-500 hover:text-red-700 border border-red-200 rounded-md px-2.5 py-2 transition-colors hover:bg-red-50 w-full" data-id="element-230">
                    Remove
                  </button>
                </div>

                {/* Organization fields */}
                {contact.entity_type === 'ORGANIZATION' && <>
                    <div className="sm:col-span-4" data-id="element-231">
                      <label className="block text-xs font-medium text-slate-600" data-id="element-232">
                        Organization Name
                      </label>
                      <input type="text" value={contact.organization_name || ''} onChange={e => updatePartyContact(group, idx, 'organization_name', e.target.value)} placeholder="Organization name" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-233" />
                    </div>
                    <div className="sm:col-span-2" data-id="element-234">
                      <label className="block text-xs font-medium text-slate-600" data-id="element-235">
                        Organization Type
                      </label>
                      <select value={contact.organization_type || ''} onChange={e => updatePartyContact(group, idx, 'organization_type', e.target.value)} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-236">
                        <option value="" data-id="element-237">Select...</option>
                        <option value="CORPORATION" data-id="element-238">Corporation</option>
                        <option value="LLC" data-id="element-239">LLC</option>
                        <option value="PARTNERSHIP" data-id="element-240">Partnership</option>
                        <option value="TRUST" data-id="element-241">Trust</option>
                        <option value="OTHER" data-id="element-242">Other</option>
                      </select>
                    </div>
                  </>}

                {/* Name fields */}
                <div className="sm:col-span-2" data-id="element-243">
                  <label className="block text-xs font-medium text-slate-600" data-id="element-244">
                    First Name
                  </label>
                  <input type="text" value={contact.first_name} onChange={e => updatePartyContact(group, idx, 'first_name', e.target.value)} placeholder="First name" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-245" />
                </div>
                <div className="sm:col-span-1" data-id="element-246">
                  <label className="block text-xs font-medium text-slate-600" data-id="element-247">
                    Middle
                  </label>
                  <input type="text" value={contact.middle_name || ''} onChange={e => updatePartyContact(group, idx, 'middle_name', e.target.value)} placeholder="MI" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-248" />
                </div>
                <div className="sm:col-span-2" data-id="element-249">
                  <label className="block text-xs font-medium text-slate-600" data-id="element-250">
                    Last Name
                  </label>
                  <input type="text" value={contact.last_name} onChange={e => updatePartyContact(group, idx, 'last_name', e.target.value)} placeholder="Last name" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-251" />
                </div>
                <div className="sm:col-span-1" data-id="element-252">
                  <label className="block text-xs font-medium text-slate-600" data-id="element-253">
                    Suffix
                  </label>
                  <input type="text" value={contact.suffix || ''} onChange={e => updatePartyContact(group, idx, 'suffix', e.target.value)} placeholder="Jr, Sr" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-254" />
                </div>

                {/* Contact fields */}
                <div className="sm:col-span-3" data-id="element-255">
                  <label className="block text-xs font-medium text-slate-600" data-id="element-256">
                    Email
                  </label>
                  <input type="email" value={contact.email} onChange={e => updatePartyContact(group, idx, 'email', e.target.value)} placeholder="email@example.com" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-257" />
                </div>
                <div className="sm:col-span-3" data-id="element-258">
                  <label className="block text-xs font-medium text-slate-600" data-id="element-259">
                    Cell Phone
                  </label>
                  <input type="text" value={contact.cell_phone} onChange={e => updatePartyContact(group, idx, 'cell_phone', e.target.value)} placeholder="(555) 123-4567" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-260" />
                </div>

                {/* SSN & On Title (for borrowers/sellers) */}
                {(group === 'borrowers' || group === 'sellers') && <>
                    <div className="sm:col-span-2" data-id="element-261">
                      <label className="block text-xs font-medium text-slate-600" data-id="element-262">
                        SSN
                      </label>
                      <input type="text" value={contact.ssn || ''} onChange={e => updatePartyContact(group, idx, 'ssn', e.target.value)} placeholder="000-00-0000" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6 font-mono" data-id="element-263" />
                    </div>
                    <div className="sm:col-span-2 flex items-end" data-id="element-264">
                      <div className="flex items-center gap-2 pb-1" data-id="element-265">
                        <button type="button" onClick={() => updatePartyContact(group, idx, 'on_title', contact.on_title ? '' : 'true')} className={`${contact.on_title ? 'bg-blue-600' : 'bg-slate-200'} relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2`} data-id="element-266">
                          <span className={`${contact.on_title ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} data-id="element-267" />
                        </button>
                        <label className="text-xs font-medium text-slate-600" data-id="element-268">
                          On Title
                        </label>
                      </div>
                    </div>
                    {group === 'borrowers' && <div className="sm:col-span-2" data-id="element-269">
                        <label className="block text-xs font-medium text-slate-600" data-id="element-270">
                          Marital Status
                        </label>
                        <select value={contact.marital_status || ''} onChange={e => updatePartyContact(group, idx, 'marital_status', e.target.value)} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-271">
                          <option value="" data-id="element-272">Select...</option>
                          <option value="MARRIED" data-id="element-273">Married</option>
                          <option value="SINGLE" data-id="element-274">Single</option>
                          <option value="DIVORCED" data-id="element-275">Divorced</option>
                          <option value="WIDOWED" data-id="element-276">Widowed</option>
                        </select>
                      </div>}
                  </>}
              </div>
            </div>)}
        </div>}
    </div>;
  // Section Definitions for Accordion
  const sections = [{
    id: 'agency',
    title: 'Settlement Agency',
    isComplete: !!selectedAgency,
    summary: selectedAgency ? `${selectedAgency.name} — ${selectedAgency.city}, ${selectedAgency.state}` : 'No agency selected',
    content: <div className="relative" ref={dropdownRef} data-id="element-277">
          <label className="block text-xs font-medium text-slate-700 mb-1.5" data-id="element-278">
            Select Agency <span className="text-red-500" data-id="element-279">*</span>
          </label>

          <button type="button" onClick={() => setIsAgencyDropdownOpen(!isAgencyDropdownOpen)} className={`relative w-full cursor-default rounded-md bg-white py-3 pl-4 pr-10 text-left text-slate-900 shadow-sm ring-1 ring-inset focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6 ${!selectedAgency ? 'ring-rose-300 bg-rose-50/30' : 'ring-slate-300'}`} data-id="element-280">
            {selectedAgency ? <div className="flex items-center justify-between" data-id="element-281">
                <div data-id="element-282">
                  <span className="block font-semibold text-slate-900" data-id="element-283">
                    {selectedAgency.name}
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5" data-id="element-284">
                    {selectedAgency.city}, {selectedAgency.state} •{' '}
                    {selectedAgency.phone}
                  </span>
                </div>
                <div className="flex gap-1.5 mr-2" data-id="element-285">
                  {selectedAgency.allowsPreOpen && <span className="inline-flex items-center rounded bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20" data-id="element-286">
                      Pre-Open
                    </span>}
                  {selectedAgency.titleSearchOnly && <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10" data-id="element-287">
                      Search Only
                    </span>}
                </div>
              </div> : <span className="block truncate text-slate-500" data-id="element-288">
                Select a settlement agency...
              </span>}
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4" data-id="element-289">
              <ChevronDownIcon className="h-5 w-5 text-slate-400" aria-hidden="true" data-id="element-290" />
            </span>
          </button>

          {isAgencyDropdownOpen && <ul className="absolute z-20 mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm" data-id="element-291">
              {mockAgencies.map(agency => <li key={agency.id} className={`relative cursor-pointer select-none py-3 pl-4 pr-9 hover:bg-slate-50 ${selectedAgency?.id === agency.id ? 'bg-blue-50/50' : ''}`} onClick={() => {
          setSelectedAgency(agency);
          setIsAgencyDropdownOpen(false);
          // Auto-advance to next section if this was the first time selecting
          if (!selectedAgency) {
            setTimeout(() => setExpandedSection('property'), 300);
          }
        }} data-id="element-292">
                  <div className="flex items-center justify-between" data-id="element-293">
                    <div data-id="element-294">
                      <span className={`block truncate ${selectedAgency?.id === agency.id ? 'font-semibold text-blue-900' : 'font-medium text-slate-900'}`} data-id="element-295">
                        {agency.name}
                      </span>
                      <span className="block truncate text-xs text-slate-500 mt-0.5" data-id="element-296">
                        {agency.city}, {agency.state} • {agency.phone}
                      </span>
                    </div>
                    <div className="flex gap-1.5" data-id="element-297">
                      {agency.allowsPreOpen && <span className="inline-flex items-center rounded bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20" data-id="element-298">
                          Pre-Open
                        </span>}
                      {agency.titleSearchOnly && <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10" data-id="element-299">
                          Search Only
                        </span>}
                    </div>
                  </div>

                  {selectedAgency?.id === agency.id && <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600" data-id="element-300">
                      <CheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-301" />
                    </span>}
                </li>)}
            </ul>}
        </div>
  }, {
    id: 'property',
    title: 'Property Information',
    isComplete: !!(property.address1 && property.city && property.state && property.zip),
    summary: property.address1 ? `${property.address1}, ${property.city} ${property.state} ${property.zip}` : 'No address entered',
    content: <div className="grid grid-cols-1 gap-y-5 gap-x-4 sm:grid-cols-6" data-id="element-302">
          <div className="sm:col-span-6" data-id="element-303">
            <label className="block text-xs font-medium text-slate-600" data-id="element-304">
              Street Address
            </label>
            <input type="text" value={property.address1} onChange={e => setProperty({
          ...property,
          address1: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-305" />
          </div>
          <div className="sm:col-span-6" data-id="element-306">
            <label className="block text-xs font-medium text-slate-600" data-id="element-307">
              Address Line 2 (Optional)
            </label>
            <input type="text" value={property.address2 || ''} onChange={e => setProperty({
          ...property,
          address2: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-308" />
          </div>
          <div className="sm:col-span-2 sm:col-start-1" data-id="element-309">
            <label className="block text-xs font-medium text-slate-600" data-id="element-310">
              City
            </label>
            <input type="text" value={property.city} onChange={e => setProperty({
          ...property,
          city: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-311" />
          </div>
          <div className="sm:col-span-1" data-id="element-312">
            <label className="block text-xs font-medium text-slate-600" data-id="element-313">
              State
            </label>
            <input type="text" value={property.state} onChange={e => setProperty({
          ...property,
          state: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-314" />
          </div>
          <div className="sm:col-span-1" data-id="element-315">
            <label className="block text-xs font-medium text-slate-600" data-id="element-316">
              ZIP
            </label>
            <input type="text" value={property.zip} onChange={e => setProperty({
          ...property,
          zip: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-317" />
          </div>
          <div className="sm:col-span-2" data-id="element-318">
            <label className="block text-xs font-medium text-slate-600" data-id="element-319">
              County
            </label>
            <input type="text" value={property.county} onChange={e => setProperty({
          ...property,
          county: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-320" />
          </div>
        </div>
  }, {
    id: 'loan',
    title: 'Loan Information',
    isComplete: !!(loan.amount && loan.number && loan.type),
    summary: `$${loan.amount.toLocaleString()} ${loan.type} ${loan.purpose} — Loan #${loan.number}`,
    content: <div className="grid grid-cols-1 gap-y-5 gap-x-4 sm:grid-cols-6" data-id="element-321">
          <div className="sm:col-span-3" data-id="element-322">
            <label className="block text-xs font-medium text-slate-600" data-id="element-323">
              Loan Number
            </label>
            <input type="text" value={loan.number} onChange={e => setLoan({
          ...loan,
          number: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6 bg-slate-50" readOnly data-id="element-324" />
          </div>
          <div className="sm:col-span-3" data-id="element-325">
            <label className="block text-xs font-medium text-slate-600" data-id="element-326">
              Loan Purpose
            </label>
            <div className="mt-1.5 flex rounded-lg bg-slate-100 p-1" data-id="element-327">
              <button type="button" onClick={() => setLoan({
            ...loan,
            purpose: 'Purchase'
          })} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${loan.purpose === 'Purchase' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-328">
                Purchase
              </button>
              <button type="button" onClick={() => setLoan({
            ...loan,
            purpose: 'Refinance'
          })} className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${loan.purpose === 'Refinance' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-329">
                Refinance
              </button>
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-330">
            <label className="block text-xs font-medium text-slate-600" data-id="element-331">
              Loan Amount
            </label>
            <div className="mt-1.5 relative rounded-lg shadow-sm" data-id="element-332">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-333">
                <span className="text-slate-500 sm:text-sm" data-id="element-334">$</span>
              </div>
              <input type="number" value={loan.amount} onChange={e => setLoan({
            ...loan,
            amount: Number(e.target.value)
          })} className="block w-full rounded-lg border-0 py-2 pl-7 pr-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-335" />
            </div>
          </div>
          {loan.purpose === 'Purchase' && <div className="sm:col-span-2" data-id="element-336">
              <label className="block text-xs font-medium text-slate-600" data-id="element-337">
                Purchase Price
              </label>
              <div className="mt-1.5 relative rounded-lg shadow-sm" data-id="element-338">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-339">
                  <span className="text-slate-500 sm:text-sm" data-id="element-340">$</span>
                </div>
                <input type="number" value={loan.purchasePrice} onChange={e => setLoan({
            ...loan,
            purchasePrice: Number(e.target.value)
          })} className="block w-full rounded-lg border-0 py-2 pl-7 pr-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-341" />
              </div>
            </div>}
          <div className="sm:col-span-2" data-id="element-342">
            <label className="block text-xs font-medium text-slate-600" data-id="element-343">
              Loan Type
            </label>
            <select value={loan.type} onChange={e => setLoan({
          ...loan,
          type: e.target.value
        })} className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-344">
              <option value="CONVENTIONAL_INSURED" data-id="element-345">Conventional</option>
              <option value="FHA" data-id="element-346">FHA</option>
              <option value="VA" data-id="element-347">VA</option>
              <option value="USDA" data-id="element-348">USDA</option>
            </select>
          </div>
          <div className="sm:col-span-3" data-id="element-349">
            <label className="block text-xs font-medium text-slate-600" data-id="element-350">
              External Reference #
            </label>
            <input type="text" value={loan.externalReferenceNumber || ''} onChange={e => setLoan({
          ...loan,
          externalReferenceNumber: e.target.value
        })} placeholder="Optional reference number" className="mt-1.5 block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" data-id="element-351" />
          </div>
          <div className="sm:col-span-3 flex items-end" data-id="element-352">
            <div className="flex items-center gap-3 pb-1" data-id="element-353">
              <button type="button" onClick={() => setLoan({
            ...loan,
            isPreopen: !loan.isPreopen
          })} className={`${loan.isPreopen ? 'bg-blue-600' : 'bg-slate-200'} relative inline-flex h-6 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2`} data-id="element-354">
                <span className={`${loan.isPreopen ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} data-id="element-355" />
              </button>
              <label className="text-xs font-medium text-slate-600" data-id="element-356">
                Pre-Open Order
              </label>
            </div>
          </div>
        </div>
  }, {
    id: 'parties',
    title: 'Parties',
    isComplete: parties.borrowers.length > 0,
    summary: `${parties.borrowers.length} Borrowers${loan.purpose === 'Purchase' ? `, ${parties.sellers.length} Sellers` : ''}${parties.lender_contacts.length > 0 ? `, ${parties.lender_contacts.length} Lender` : ''}${parties.listing_agents.length > 0 ? `, ${parties.listing_agents.length} Listing Agent` : ''}${parties.selling_agents.length > 0 ? `, ${parties.selling_agents.length} Selling Agent` : ''}`,
    content: <div className="space-y-8" data-id="element-357">
          {/* Borrowers */}
          {renderPartyGroup('Borrowers', 'borrowers', 'border-l-blue-500')}

          {/* Sellers (Purchase only) */}
          {loan.purpose === 'Purchase' && <>
              <div className="h-px bg-slate-200 w-full" data-id="element-358" />
              {renderPartyGroup('Sellers', 'sellers', 'border-l-orange-500')}
            </>}

          {/* Lender Contacts */}
          <div className="h-px bg-slate-200 w-full" data-id="element-359" />
          {renderPartyGroup('Lender Contacts', 'lender_contacts', 'border-l-emerald-500')}

          {/* Listing Agents */}
          <div className="h-px bg-slate-200 w-full" data-id="element-360" />
          {renderPartyGroup('Listing Agents', 'listing_agents', 'border-l-purple-500')}

          {/* Selling Agents */}
          <div className="h-px bg-slate-200 w-full" data-id="element-361" />
          {renderPartyGroup('Selling Agents', 'selling_agents', 'border-l-indigo-500')}
        </div>
  }, {
    id: 'notes',
    title: 'Notes & Attachments',
    isComplete: true,
    summary: notes ? 'Notes added' : 'No notes',
    content: <div className="space-y-5" data-id="element-362">
          <div data-id="element-363">
            <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-364">
              Order Notes / Memo
            </label>
            <textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} className="block w-full rounded-lg border-0 py-2 px-3 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:leading-6" placeholder="Any special instructions for the title agency..." data-id="element-365" />
          </div>

          <div data-id="element-366">
            <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-367">
              Documents
            </label>
            <div className="flex justify-center rounded-lg border border-dashed border-slate-300 px-6 py-8 hover:bg-slate-50 transition-colors cursor-pointer bg-white" data-id="element-368">
              <div className="text-center" data-id="element-369">
                <UploadIcon className="mx-auto h-8 w-8 text-slate-300" aria-hidden="true" data-id="element-370" />
                <div className="mt-3 flex text-sm leading-6 text-slate-600 justify-center" data-id="element-371">
                  <span className="relative cursor-pointer rounded-md bg-transparent font-medium text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-500" data-id="element-372">
                    Upload a file
                  </span>
                  <p className="pl-1" data-id="element-373">or drag and drop</p>
                </div>
                <p className="text-xs leading-5 text-slate-500 mt-1" data-id="element-374">
                  PDF, DOCX up to 10MB
                </p>
              </div>
            </div>
          </div>
        </div>
  }];
  return <div className="flex flex-col h-full bg-slate-50 overflow-hidden" data-id="element-375">
      <div className="flex-1 overflow-y-auto p-6 scroll-smooth" data-id="element-376">
        <div className="max-w-3xl mx-auto" data-id="element-377">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 divide-y divide-slate-200" data-id="element-378">
            {sections.map((section, index) => {
            const isExpanded = expandedSection === section.id;
            const stepNumber = index + 1;
            return <div key={section.id} className="flex flex-col" data-id="element-379">
                  {/* Accordion Header */}
                  <div className="cursor-pointer px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" onClick={() => toggleSection(section.id)} data-id="element-380">
                    <div className="flex items-center gap-3" data-id="element-381">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${section.isComplete ? 'bg-emerald-100 text-emerald-700' : isExpanded ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-382">
                        {section.isComplete ? <CheckIcon className="h-4 w-4" data-id="element-383" /> : stepNumber}
                      </div>
                      <h2 className={`text-sm font-semibold transition-colors ${isExpanded ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-384">
                        {section.title}
                      </h2>
                    </div>

                    <div className="flex items-center gap-3" data-id="element-385">
                      {!isExpanded && <span className="text-xs text-slate-500 truncate max-w-[200px] sm:max-w-xs hidden sm:block" data-id="element-386">
                          {section.summary}
                        </span>}
                      <ChevronRightIcon className={`h-5 w-5 text-slate-400 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-387" />
                    </div>
                  </div>

                  {/* Accordion Body */}
                  <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-388">
                    <div className="px-6 pb-6 pt-3" data-id="element-389">{section.content}</div>
                  </div>
                </div>;
          })}
          </div>

          {/* Submit Button Area */}
          <div className="mt-8 flex justify-end" data-id="element-390">
            <button onClick={handleSubmit} disabled={isSubmitting || !selectedAgency} className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-391">
              {isSubmitting ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-392">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-393"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-394"></path>
                  </svg>
                  Submitting...
                </> : 'Submit Title Order'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}